import {
  Component,
  ElementRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { Duration, parseISO8601Duration, toISO8601Duration } from '@core/utils/time-utils';

import {
  FieldDate,
  FormBuilderBase
} from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { SelectItem } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { unwrapValue, wrapValue } from '../row-edition-form.utils';

@Component({
  selector: 'app-duration',
  imports: [SelectComponent],
  templateUrl: './duration.component.html',
  styles: [
    `
      :host {
        display: flex;
        width: 100%;
        gap: 4px;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DurationComponent),
      multi: true
    }
  ]
})
export class DurationComponent implements ControlValueAccessor {
  id = input<FormBuilderBase['id']>();
  value = model.required<string | null>();
  min = input<FieldDate['minDate']>();
  max = input<FieldDate['maxDate']>();
  required = input<FormBuilderBase['required']>();
  needTime = input<FieldDate['needTime']>();
  wrapperProperty = input<string | undefined>(undefined);
  isFormRequired = input<boolean>();

  protected unit = signal<string>('seconds');
  protected numberValue = signal<number>(0);

  protected numberInput = viewChild<ElementRef<HTMLInputElement>>('numberInput');

  private translate = inject(TranslateService);

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected availableUnits = [
    'seconds',
    'minutes',
    'hours',
    'days',
    'weeks',
    'months',
    'years'
  ];

  protected unitItems = computed<SelectItem[]>(() =>
    this.translatedUnits().map((unit) => ({ id: unit.value, label: unit.label }))
  );

  protected translatedUnits = computed(() => {
    // Trigger on language change
    this.currentLang();

    return this.availableUnits.map((unit) => ({
      value: unit,
      label: this.translate.instant(`common.time_units.${unit}`)
    }));
  });

  constructor() {
    effect(() => {
      const value = this.value();
      if (!isNil(value)) {
        const isoDuration = parseISO8601Duration(value);
        const converted = this.convertDurationToNumberAndUnit(isoDuration);
        if (converted) {
          this.numberValue.set(converted.value);
          this.unit.set(converted.unit);
        }
      }
      this.onChange(value);
    });
  }

  convertDurationToNumberAndUnit(
    duration: Duration
  ): { value: number; unit: string } | null {
    const totalSeconds = duration.totalMilliseconds / 1000;

    if (totalSeconds === 0) {
      return null;
    }

    const conversions = [
      { unit: 'years', seconds: 31536000 },
      { unit: 'months', seconds: 2592000 },
      { unit: 'weeks', seconds: 604800 },
      { unit: 'days', seconds: 86400 },
      { unit: 'hours', seconds: 3600 },
      { unit: 'minutes', seconds: 60 },
      { unit: 'seconds', seconds: 1 }
    ];

    for (const conv of conversions) {
      if (totalSeconds >= conv.seconds) {
        return {
          value: totalSeconds / conv.seconds,
          unit: conv.unit
        };
      }
    }

    return { value: totalSeconds, unit: 'seconds' };
  }

  convertNumberAndUnitToDuration(value: number, unit: string): Duration {
    const conversionsToSeconds: { [key: string]: number } = {
      seconds: 1,
      minutes: 60,
      hours: 3600,
      days: 86400,
      weeks: 604800,
      months: 2592000,
      years: 31536000
    };

    return { totalMilliseconds: value * conversionsToSeconds[unit] * 1000 };
  }

  onChange = (_value: string | null) => {};
  onTouched = () => {};

  protected onUnitChange(unit: string) {
    this.unit.set(unit);
    this.onInput();
  }

  onInput() {
    const unitValue = this.unit();
    const numberValue = parseFloat(this.numberInput()!.nativeElement.value || '0');

    const iso8601Duration = toISO8601Duration(
      this.convertNumberAndUnitToDuration(numberValue, unitValue)
    );
    this.onChange(iso8601Duration);
  }

  writeValue(value: string): void {
    this.value.set(unwrapValue(value, this.wrapperProperty()) as string);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = (value: string | null) => fn(wrapValue(value, this.wrapperProperty()));
  }
  registerOnTouched(fn: never): void {
    this.onTouched = fn;
  }
}
